import { motion } from "framer-motion";
import { 
  Beaker, 
  Cpu, 
  Shield, 
  Globe, 
  Zap, 
  TrendingUp, 
  Calendar, 
  FlaskConical, 
  Binary, 
  Atom, 
  Users, 
  Briefcase,
  ExternalLink
} from "lucide-react";
import { IMAGES } from "@/assets/images";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  QuantumMilestone, 
  QuantumApplication, 
  BahrainInitiative, 
  getCategoryColor 
} from "@/lib/index";
import { cn } from "@/lib/utils";

interface MilestoneCardProps {
  milestone: QuantumMilestone;
}

export function MilestoneCard({ milestone }: MilestoneCardProps) {
  const categoryColor = getCategoryColor(milestone.category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="group relative overflow-hidden border-border/40 bg-card/60 backdrop-blur-md transition-all hover:border-primary/50 hover:shadow-[0_0_30px_-10px_oklch(var(--primary)/0.3)]">
        <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-primary/5 blur-3xl transition-all group-hover:bg-primary/10" />
        
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className={cn("font-mono text-3xl font-bold tracking-tighter", categoryColor)}>
              {milestone.year}
            </span>
            <Badge variant="outline" className={cn("capitalize border-current", categoryColor)}>
              {milestone.category}
            </Badge>
          </div>
          <CardTitle className="text-xl font-bold tracking-tight">{milestone.title}</CardTitle>
          {milestone.scientist && (
            <CardDescription className="flex items-center gap-1.5 font-medium text-foreground/70">
              <Users className="h-3.5 w-3.5" />
              {milestone.scientist}
            </CardDescription>
          )}
        </CardHeader>
        
        <CardContent>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {milestone.description}
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface ApplicationCardProps {
  application: QuantumApplication;
}

export function ApplicationCard({ application }: ApplicationCardProps) {
  const industryIcons: Record<string, any> = {
    Medicine: Beaker,
    Computing: Cpu,
    Security: Shield,
    Communication: Globe,
    Energy: Zap,
    Finance: TrendingUp,
  };

  const Icon = industryIcons[application.industry] || Binary;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="h-full"
    >
      <Card className="flex h-full flex-col border-border/40 bg-card/40 backdrop-blur-md overflow-hidden">
        <div className="relative h-48 w-full overflow-hidden">
          <img 
            src={IMAGES.QUANTUM_COMPUTING_1} 
            alt={application.title} 
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          <div className="absolute top-4 left-4">
            <Badge className="bg-primary text-primary-foreground">
              {application.industry}
            </Badge>
          </div>
        </div>
        
        <CardHeader className="pt-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg leading-tight">{application.title}</CardTitle>
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Impact: {application.impact}
              </span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col justify-between gap-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {application.description}
          </p>
          {application.bahrainUseCase && (
            <div className="mt-auto rounded-lg bg-accent/5 p-3 border border-accent/10">
              <p className="text-xs font-semibold text-accent mb-1 uppercase tracking-wider">Bahrain Application</p>
              <p className="text-xs text-foreground/80">{application.bahrainUseCase}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface InitiativeCardProps {
  initiative: BahrainInitiative;
}

export function InitiativeCard({ initiative }: InitiativeCardProps) {
  const statusColors: Record<string, string> = {
    Active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    Upcoming: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    "Strategic Partnership": "bg-primary/10 text-primary border-primary/20",
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
    >
      <Card className="group relative overflow-hidden border-border/40 bg-card/60 backdrop-blur-md">
        <div className="flex flex-col md:flex-row">
          <div className="relative w-full md:w-48 overflow-hidden">
            <img 
              src={IMAGES.RESEARCH_COLLABORATION_1} 
              alt={initiative.title} 
              className="h-full w-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-background hidden md:block" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background md:hidden" />
          </div>
          
          <div className="flex-1 p-6">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge variant="outline" className={cn("px-2 py-0", statusColors[initiative.status])}>
                {initiative.status}
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-1 font-mono">
                <Calendar className="h-3 w-3" /> {initiative.establishedYear}
              </Badge>
            </div>

            <div className="flex justify-between items-start gap-4 mb-2">
              <div>
                <h3 className="text-xl font-bold tracking-tight group-hover:text-primary transition-colors">
                  {initiative.title}
                </h3>
                <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Briefcase className="h-3.5 w-3.5" /> Partner: {initiative.partner}
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              {initiative.description}
            </p>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-background/50">
                {initiative.focus}
              </Badge>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

interface TimelineCardProps {
  milestone: QuantumMilestone;
  index: number;
}

export function TimelineCard({ milestone, index }: TimelineCardProps) {
  const isEven = index % 2 === 0;
  const categoryColor = getCategoryColor(milestone.category);

  return (
    <div className={cn("relative flex w-full mb-12 md:mb-24", isEven ? "justify-start" : "justify-end")}>
      {/* Timeline Node */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2 z-10 hidden md:block">
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          className={cn("h-12 w-12 rounded-full border-4 border-background bg-primary shadow-[0_0_15px_oklch(var(--primary)/0.5)] flex items-center justify-center text-primary-foreground")}
        >
          <Atom className="h-6 w-6 animate-spin-slow" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: isEven ? -50 : 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="w-full md:w-[45%]"
      >
        <Card className="relative overflow-hidden border-border/40 bg-card/60 backdrop-blur-md hover:border-primary/40 transition-all">
          <div className={cn("absolute top-0 left-0 w-1.5 h-full", categoryColor.replace('text', 'bg'))} />
          
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <span className={cn("font-mono text-2xl font-bold", categoryColor)}>
                {milestone.year}
              </span>
              {milestone.isMajor && (
                <Badge className="bg-accent text-accent-foreground animate-pulse">
                  Centennial Landmark
                </Badge>
              )}
            </div>
            <CardTitle className="text-lg">{milestone.title}</CardTitle>
            {milestone.scientist && (
              <CardDescription className="italic">
                — {milestone.scientist}
              </CardDescription>
            )}
          </CardHeader>
          
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {milestone.description}
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
